import React from 'react';
import { useIntl } from 'react-intl';

interface StatusBoxProps {
    status: any;
    isAdmin: boolean;
    isSelected?: boolean;
    dimmed?: boolean;
    onClick?: () => void;
}

const StatusBox: React.FC<StatusBoxProps> = ({ status, isAdmin, isSelected, dimmed, onClick }) => {
    const intl = useIntl();
    const { statusInfo, responsibilityName, hasMaterials, completedMaterials, materialCount, adminWarning } = status;

    const hasLateWarning = status.warning
        || status.responsibilityStatusId === 3
        || (status.materialStatuses || []).some((m: any) => m.warning || m.responsibilityStatusId === 3);

    // A status counts as "returned to production" when itself or any of its material statuses
    // has any row in process_return_history — both admin_return and worker_block events qualify.
    const hasReturned = (status.returnCount || 0) > 0
        || (status.materialStatuses || []).some((m: any) => (m.returnCount || 0) > 0);

    // Admin warning text alone (no late state, no return event) only surfaces as a red-icon
    // trigger for admins. Non-admins still see the text in the tooltip when another signal
    // (late / returned) brings the icon up, matching prior behavior.
    const hasAdminWarningVisible = !!(adminWarning && isAdmin);

    const defaultReason = intl.formatMessage({ id: 'production.statusBox.defaultReason', defaultMessage: 'Kthyer në prodhim' });
    const latestWarning = status.warning
        || status.adminWarning
        || (status.materialStatuses || []).find((m: any) => m.warning || m.adminWarning)?.warning
        || (status.materialStatuses || []).find((m: any) => m.adminWarning)?.adminWarning
        || defaultReason;

    const showRedIcon = hasLateWarning || hasReturned || hasAdminWarningVisible;
    const hasComment = !showRedIcon && status.latestComment;

    const showIcon = showRedIcon || hasComment;
    const iconClass = showRedIcon ? 'npb-info-red' : 'npb-info-blue';
    const iconTitle = showRedIcon ? latestWarning : (status.latestComment || '');

    const buildTooltip = () => {
        const parts: string[] = [responsibilityName];
        // The box text is the process name, so without this the tooltip never said which status
        // the process is in — and the four pause reasons were indistinguishable from each other.
        parts.push(intl.formatMessage({ id: `production.status.${statusInfo.key}`, defaultMessage: statusInfo.label }));
        // "Arsye tjetër" (status 8) carries a free-text reason — surface it right here.
        if (status.otherComment) {
            parts.push(intl.formatMessage({ id: 'production.statusBox.pauseReason', defaultMessage: 'Arsyeja: {reason}' }, { reason: status.otherComment }));
        }
        if (status.userName && status.userName !== 'null') {
            parts.push(intl.formatMessage({ id: 'production.statusBox.responsible', defaultMessage: 'Përgjegjës: {name}' }, { name: status.userName }));
        }
        if (hasMaterials) {
            parts.push(intl.formatMessage({ id: 'production.statusBox.materials', defaultMessage: 'Materiale: {completed}/{total}' }, { completed: completedMaterials, total: materialCount }));
            (status.materialStatuses || []).forEach((m: any) => {
                const mName = m.materialName || intl.formatMessage({ id: 'production.material.default', defaultMessage: 'Material' });
                const mUser = m.userName ? ` (${m.userName})` : '';
                parts.push(`  • ${mName}${mUser}`);
            });
        }
        return parts.join('\n');
    };

    return (
        <div
            className={`npb-status-box npb-status-${statusInfo.key} ${isSelected ? 'selected' : ''} ${dimmed ? 'npb-dimmed' : ''}`}
            title={buildTooltip()}
            onClick={dimmed ? undefined : onClick}
        >
            <span className="npb-status-name">{responsibilityName}</span>
            {hasMaterials && (
                <span className="npb-status-materials">{completedMaterials}/{materialCount}</span>
            )}
            {showIcon && (
                <span className={`npb-status-info-icon ${iconClass}`} title={iconTitle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                </span>
            )}
        </div>
    );
};

export default StatusBox;
